/**
 * WebSocket Service for real-time communication with Peter Bot
 * Handles Socket.io client connections and event listeners
 */

import { io } from 'socket.io-client';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000; // Start with 1 second
    this.currentConversationId = null;
    this.currentToken = null;
    this.currentUserId = null;
  }

  /**
   * Connect to WebSocket server
   * @param {string} conversationId - Conversation ID
   * @param {string} token - Chat token for authentication
   * @param {string} userId - User ID (optional, for joining user room)
   */
  connect(conversationId, token, userId = null) {
    if (!conversationId || !token) {
      console.warn('[WebSocket] Cannot connect: missing conversationId or token');
      return;
    }

    // Check if we need to re-authenticate for a different conversation
    if (this.socket && this.socket.connected) {
      // If conversationId, token, or userId changed, re-authenticate
      if (this.currentConversationId !== conversationId || this.currentToken !== token || this.currentUserId !== userId) {
        console.log('[WebSocket] Re-authenticating for new conversation', {
          oldConversationId: this.currentConversationId,
          newConversationId: conversationId,
          userId: userId
        });
        this.currentConversationId = conversationId;
        this.currentToken = token;
        this.currentUserId = userId;
        // Re-authenticate with new conversation
        this.socket.emit('authenticate', { conversationId, token, userId });
        return; // Return early, authentication will complete asynchronously
      }
      // Already connected and authenticated for this conversation
      console.log('[WebSocket] Already connected and authenticated for this conversation:', conversationId);
      return;
    }

    // Store current conversation info
    this.currentConversationId = conversationId;
    this.currentToken = token;
    this.currentUserId = userId;

    // Get API URL from environment or use default
    const apiUrl = process.env.REACT_APP_PETER_BOT_API_URL || 'http://localhost:5002';

    console.log(`[WebSocket] Connecting to ${apiUrl}...`);

    this.socket = io(apiUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: this.reconnectDelay,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelayMax: 5000,
      timeout: 20000
    });

    // Connection event handlers
    this.socket.on('connect', () => {
      console.log('[WebSocket] Connected:', this.socket.id);
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;

      // Authenticate with current conversation ID and token (use stored values in case of reconnection)
      const convId = this.currentConversationId || conversationId;
      const convToken = this.currentToken || token;
      const userIdToUse = userId || this.currentUserId;
      console.log('[WebSocket] Authenticating on connect:', { conversationId: convId, userId: userIdToUse });
      this.socket.emit('authenticate', { conversationId: convId, token: convToken, userId: userIdToUse });
    });

    this.socket.on('authenticated', (data) => {
      console.log('[WebSocket] Authenticated for conversation:', data.conversationId, {
        currentConversationId: this.currentConversationId,
        socketId: this.socket.id
      });
      // Update stored conversation info to match authenticated conversation
      if (data.conversationId) {
        this.currentConversationId = data.conversationId;
      }
      this.emit('connected', data);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[WebSocket] Disconnected:', reason);
      this.isConnected = false;
      this.emit('disconnected', { reason });
    });

    this.socket.on('connect_error', (error) => {
      console.error('[WebSocket] Connection error:', error);
      this.reconnectAttempts++;
      this.emit('error', { error, reconnectAttempts: this.reconnectAttempts });
    });

    this.socket.on('error', (error) => {
      console.error('[WebSocket] Error:', error);
      this.emit('error', { error });
    });

    // Message event handlers
    this.socket.on('message:new', (data) => {
      console.log('[WebSocket] New message received:', data);
      this.emit('message:new', data);
    });

    this.socket.on('conversation:updated', (data) => {
      console.log('[WebSocket] Conversation updated:', data);
      this.emit('conversation:updated', data);
    });

    this.socket.on('topic:resolved', (data) => {
      console.log('[WebSocket] Topic resolved:', data);
      this.emit('topic:resolved', data);
    });
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    if (this.socket) {
      console.log('[WebSocket] Disconnecting...');
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.currentConversationId = null;
      this.currentToken = null;
      this.currentUserId = null;
      this.listeners.clear();
    }
  }

  /**
   * Subscribe to an event
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);

    // Return unsubscribe function
    return () => {
      const callbacks = this.listeners.get(event);
      if (callbacks) {
        callbacks.delete(callback);
      }
    };
  }

  /**
   * Unsubscribe from an event
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  off(event, callback) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.delete(callback);
    }
  }

  /**
   * Emit event to listeners
   * @param {string} event - Event name
   * @param {*} data - Event data
   */
  emit(event, data) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`[WebSocket] Error in callback for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Get connection status
   * @returns {boolean} True if connected
   */
  getConnectionStatus() {
    return this.isConnected && this.socket && this.socket.connected;
  }
}

// Export singleton instance
const websocketService = new WebSocketService();
export default websocketService;

